/**
 * Shared date range filter – calendar range, applies start/end keys.
 * Supports useStore (filters[startKey], filters[endKey], setFilters) or controlled (filter, onFilterChange).
 */

import { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { CalendarIcon } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, formatDateToISO, formatDateToYYYYMMDD, getEndOfDayISO } from '@/modules/shared/utils';
import { useTranslation } from '@/modules/shared/i18n/useTranslation';
import { en as sharedEn, th as sharedTh } from '@/modules/shared/i18n/locales';
import type { DateRange } from 'react-day-picker';
import { testIds } from '@/test/testIds';

export interface DateRangeFilterUseStore {
  (): { filters: Record<string, unknown>; setFilters: (updates: Record<string, unknown>) => void };
}

const OUTSIDE_DAY_STYLES = `
  [data-slot="calendar"] [class*="outside"],
  [data-slot="calendar"] [class*="outside"] button,
  [data-slot="calendar"] [class*="outside"] button[data-range-start],
  [data-slot="calendar"] [class*="outside"] button[data-range-end],
  [data-slot="calendar"] [class*="outside"] button[data-range-middle],
  [data-slot="calendar"] [class*="outside"] button:hover,
  [data-slot="calendar"] [class*="outside"] button:focus,
  [data-slot="calendar"] [class*="outside"] button:active {
    background-color: transparent !important;
    background: transparent !important;
  }
  [data-slot="calendar"] [class*="outside"] button {
    color: hsl(var(--muted-foreground)) !important;
  }
`;

const parseISODate = (dateStr: string): Date => new Date(dateStr);

export interface DateRangeFilterProps {
  useStore?: DateRangeFilterUseStore;
  startDateKey?: string;
  endDateKey?: string;
  filter?: Record<string, unknown>;
  onFilterChange?: (updates: Record<string, unknown>) => void;
  label?: string;
  useYYYYMMDD?: boolean;
  renderTrigger?: (props: { onClick: () => void }) => React.ReactNode;
  /**
   * Hard cap on the span between `from` and `to`, in months. When set, a range
   * exceeding this is rejected with a toast and the selection is reverted.
   * Inclusive: a span of exactly N months is allowed.
   */
  maxRangeMonths?: number;
  testIdPrefix?: string;
}

export function DateRangeFilter({
  useStore,
  startDateKey = 'policy_end_date_after',
  endDateKey = 'policy_end_date_before',
  filter,
  onFilterChange,
  label,
  useYYYYMMDD = false,
  renderTrigger,
  maxRangeMonths,
  testIdPrefix,
}: DateRangeFilterProps) {
  const [open, setOpen] = useState(false);
  const { t, locale } = useTranslation({ en: sharedEn, th: sharedTh });
  const store = useStore?.();
  const isControlled = filter != null && onFilterChange != null;
  const filters = isControlled ? filter : (store?.filters ?? {});
  const setFilters = isControlled ? onFilterChange! : (store?.setFilters ?? (() => {}));

  const usePolicyEndDateFormat = startDateKey === 'policy_end_date_after' && endDateKey === 'policy_end_date_before';
  const formatDateForAPI = useYYYYMMDD || usePolicyEndDateFormat ? formatDateToYYYYMMDD : formatDateToISO;
  const formatEndDateForAPI =
    useYYYYMMDD || usePolicyEndDateFormat
      ? formatDateToYYYYMMDD
      : (date: Date) => getEndOfDayISO(formatDateToYYYYMMDD(date));

  const startStr = filters[startDateKey] as string | undefined;
  const endStr = filters[endDateKey] as string | undefined;
  const startDate = startStr ? parseISODate(startStr) : undefined;
  const endDate = endStr ? parseISODate(endStr) : undefined;
  const committedRange: DateRange | undefined = startDate || endDate ? { from: startDate, to: endDate } : undefined;

  // Pending range lives locally so intermediate clicks during range selection
  // don't fire downstream queries. We commit on complete-range or popover close.
  const [pendingRange, setPendingRange] = useState<DateRange | undefined>(committedRange);

  const commit = (range: DateRange | undefined) => {
    setFilters({
      [startDateKey]: range?.from ? formatDateForAPI(range.from) : undefined,
      [endDateKey]: range?.to ? formatEndDateForAPI(range.to) : undefined,
    });
  };

  const sameDay = (a?: Date, b?: Date) => !!a && !!b && a.toDateString() === b.toDateString();

  /**
   * True when `to` is strictly more than `maxRangeMonths` calendar months after `from`.
   * Counts inclusive — a span equal to the limit is allowed.
   */
  const exceedsMaxRange = (from: Date, to: Date, maxMonths: number): boolean => {
    const limit = new Date(from);
    limit.setMonth(limit.getMonth() + maxMonths);
    return to.getTime() > limit.getTime();
  };

  const handleSelect = (range: DateRange | undefined) => {
    const isCompleteRange = range?.from && range?.to && !sameDay(range.from, range.to);

    if (isCompleteRange && maxRangeMonths != null && exceedsMaxRange(range!.from!, range!.to!, maxRangeMonths)) {
      toast.error(t('dateRangeMaxExceeded', { months: maxRangeMonths }));
      // Revert to the previously committed range; keep popover open so the user can retry.
      setPendingRange(committedRange);
      return;
    }

    setPendingRange(range);
    if (isCompleteRange) {
      commit(range);
      setTimeout(() => setOpen(false), 100);
    }
  };

  const handleOpenChange = (next: boolean) => {
    // Opening starts from the committed value.
    if (next) setPendingRange(committedRange);
    setOpen(next);
    if (!next) {
      // Commit pending state on close if it differs from what's already committed.
      const pendingFromStr = pendingRange?.from ? formatDateForAPI(pendingRange.from) : undefined;
      const pendingToStr = pendingRange?.to ? formatEndDateForAPI(pendingRange.to) : undefined;
      if (pendingFromStr !== startStr || pendingToStr !== endStr) {
        commit(pendingRange);
      }
    }
  };

  const selectedRange = open ? pendingRange : committedRange;

  const getDisplayText = (): string => {
    if (!startDate && !endDate) return t('dateRangeEmpty');
    // Day + short month only ("4 Aug" / "4 ส.ค.") — no year, so the Thai Buddhist era never shows up.
    const day = (str: string | undefined) =>
      str ? formatDate(str, locale === 'th' ? 'th-TH' : 'en-GB', { day: 'numeric', month: 'short' }) : '--';
    return `${day(startStr)} ${t('dateRangeTo')} ${day(endStr)}`;
  };

  const defaultMonth = startDate || new Date();

  return (
    <>
      <style>{OUTSIDE_DAY_STYLES}</style>
      <Popover open={open} onOpenChange={handleOpenChange}>
        {renderTrigger ? (
          <PopoverTrigger asChild>
            <div>
              {renderTrigger({
                onClick: () => {
                  if (!open) setPendingRange(committedRange);
                  setOpen(!open);
                },
              })}
            </div>
          </PopoverTrigger>
        ) : (
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="px-3 rounded-lg"
              {...(testIdPrefix ? { 'data-testid': testIds.filters.dateRange(testIdPrefix) } : {})}
            >
              <CalendarIcon className="w-4 h-4 mr-2" />
              {label ?? t('createdOn')} | {getDisplayText()}
            </Button>
          </PopoverTrigger>
        )}
        <PopoverContent className="w-auto p-0 max-h-none overflow-visible" align="start">
          <Calendar
            mode="range"
            selected={selectedRange}
            onSelect={handleSelect}
            numberOfMonths={2}
            defaultMonth={defaultMonth}
            classNames={{
              day: 'group/day relative aspect-square h-full w-full select-none p-0 text-center [&:first-child[data-selected=true]_button]:rounded-l-md [&:last-child[data-selected=true]_button]:rounded-r-md',
              range_start: 'bg-primary rounded-l-md',
              range_middle: 'bg-primary/10 rounded-none',
              range_end: 'bg-primary rounded-r-md',
              outside: 'date-range-filter-outside-day text-muted-foreground',
            }}
          />
        </PopoverContent>
      </Popover>
    </>
  );
}

export default DateRangeFilter;
